<template>
  <div>
    <v-container>
      <h1>Remarket Processing</h1>
      <p>
        Remarkets Vave Homeowners policies into new submissions seeded from the current policy, keeping the original agent,
        starting when the current term expires, and quoting with the sales channels you choose.
      </p>

      <v-card class="mb-4">
        <v-card-title>
          Remarket Policies
        </v-card-title>
        <v-card-text>
          <!-- plain textarea: FormTextArea flattens newlines and strips trailing spaces as you type, which breaks pasted lists -->
          <v-textarea v-model="submissionIdsInput"
                      label="Submission Ids"
                      placeholder="Paste the bound submission ids, one per line or comma separated (e.g. SUB000012345)"
                      outlined
                      auto-grow
                      clearable
                      rows="6"
                      :disabled="loading"
                      data-testid="remarketSubmissionIds" />

          <div class="mb-2" data-testid="remarketSubmissionCount">
            {{ parsed.valid.length }} submission{{ parsed.valid.length === 1 ? '' : 's' }} to remarket
          </div>
          <v-alert v-if="parsed.invalid.length" type="error" dense outlined data-testid="remarketInvalidIds">
            These aren't submission ids: {{ parsed.invalid.join(', ') }}
          </v-alert>
          <v-alert v-if="tooMany" type="error" dense outlined>
            A run is limited to {{ maxSubmissions }} submissions.
          </v-alert>

          <div class="mb-4" data-testid="remarketSalesChannels">
            <div class="subtitle-2">Sales channels to quote with</div>
            <v-checkbox v-for="channel in salesChannelOptions"
                        :key="channel.value"
                        v-model="selectedSalesChannels"
                        :value="channel.value"
                        :label="channel.label"
                        :disabled="loading"
                        dense
                        hide-details
                        class="mt-1" />
            <div v-if="!selectedSalesChannels.length" class="caption mt-2">Choose at least one sales channel</div>
          </div>

          <FormButton label="Begin" size="x-large" @click="runRemarket" :disabled="!canRun"></FormButton>
        </v-card-text>
      </v-card>

      <v-row>
        <v-col cols="3">
          <v-card class="mb-4">
            <v-card-text class="text-center">
              <h2 class="mb-2">Processing Remarket Jobs:</h2>
              <h1 class="mb-0">{{ processingCount }}</h1>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="3">
          <v-card class="mb-4">
            <v-card-text class="text-center">
              <h2 class="mb-2">Queued Remarket Jobs:</h2>
              <h1 class="mb-0">{{ enqueuedCount }}</h1>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <p>
        <FormButton label="Refresh Data" size="x-large" @click="getData" :disabled="loading"></FormButton>
      </p>

      <v-card class="mb-4">
        <v-card-title>
          Failed Remarket Jobs ({{ failedJobs.length }})
        </v-card-title>
        <v-card-text>
          <ResultsTable :tableHeaders="failedJobsHeaders"
                        :results="failedJobs"
                        :loading="loading">

            <template v-slot:FailedAt="{item}">
              <span>{{ formatDateTimeForDisplay(item.FailedAt) }}</span>
            </template>

            <template v-slot:SubmissionId="{item}">
              <span>{{ item.SubmissionId }}</span>
            </template>

            <template v-slot:Exception="{item}">
              <span>{{ item.Exception }}</span>
            </template>

            <template v-slot:Actions="{item}">
              <div class="d-flex">
                <FormButton label="Reprocess" size="medium" class="mr-2" @click="reprocessFailedJob(item.Key)" :disabled="loading"></FormButton>
                <FormButton label="Remove" size="medium" @click="jobToRemove = item" :disabled="loading" data-testid="removeFailedJob"></FormButton>
              </div>
            </template>

          </ResultsTable>
        </v-card-text>
      </v-card>

      <!-- for a job that won't succeed however often it's reprocessed, e.g. "No policy found for submission" -->
      <v-dialog :value="jobToRemove !== null" max-width="560" @input="open => { if (!open) jobToRemove = null; }">
        <v-card v-if="jobToRemove">
          <v-card-title>Remove the failed remarket job for {{ jobToRemove.SubmissionId }}?</v-card-title>
          <v-card-text>
            <p>It's deleted from Hangfire and can't be reprocessed afterwards; remarket the submission again to retry it.</p>
            <p class="mb-0"><strong>Reason it failed:</strong> {{ jobToRemove.Exception }}</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="jobToRemove = null">Cancel</v-btn>
            <v-btn color="error" :disabled="loading" @click="removeFailedJob" data-testid="removeFailedJobConfirm">Remove</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
  import FormButton from '@/components/form/FormButton.vue';
  import ResultsTable from '@/components/shared/ResultsTable.vue';
  import { formatDateTimeForDisplay } from '@/scripts/helper';
  import { MAX_REMARKET_SUBMISSIONS, REMARKET_SALES_CHANNELS, parseSubmissionIds } from '@/scripts/remarket';
  import { HangfireService } from '@/api/HangfireService';
  import { RemarketService } from '@/api/RemarketService';

  export default {
    name: 'RemarketProcessing',
    components: {
      FormButton,
      ResultsTable
    },
    data () {
      return {
        loading: false,
        maxSubmissions: MAX_REMARKET_SUBMISSIONS,
        submissionIdsInput: '',
        salesChannelOptions: REMARKET_SALES_CHANNELS,
        // none chosen by default, so a run never quotes markets nobody picked
        selectedSalesChannels: [],
        enqueuedCount: 0,
        scheduledCount: 0,
        processingCount: 0,
        failedJobs: [],
        jobToRemove: null,
        failedJobsHeaders: [
          { text: "Failed At", value: "FailedAt", sortable: true, filterable: false },
          { text: "Submission Id", value: "SubmissionId", sortable: true, filterable: true },
          { text: "Reason", value: "Exception", sortable: true, filterable: true },
          { text: "", value: "Actions", sortable: false, filterable: false }
        ]
      };
    },
    created () {
      this.getData();
    },
    computed: {
      parsed () {
        return parseSubmissionIds(this.submissionIdsInput);
      },
      tooMany () {
        return this.parsed.valid.length > this.maxSubmissions;
      },
      canRun () {
        return !this.loading && this.parsed.valid.length > 0 && this.parsed.invalid.length === 0 && !this.tooMany &&
          this.selectedSalesChannels.length > 0;
      }
    },
    methods: {
      formatDateTimeForDisplay,
      // the axios plugin shows the error toast for a failed request; the catches here only keep the page usable
      getData () {
        this.loading = true;
        RemarketService.getJobs()
          .then((result) => {
            this.enqueuedCount = result.EnqueuedCount;
            this.scheduledCount = result.ScheduledCount;
            this.processingCount = result.ProcessingCount;
            this.failedJobs = result.FailedJobs;
          })
          .catch(() => {})
          .finally(() => {
            this.loading = false;
          });
      },
      reprocessFailedJob (key) {
        // block a second click while the first is in flight, so the same job isn't requeued twice
        this.loading = true;
        HangfireService.reprocessJob({ key })
          .then(() => {
            this.$store.dispatch('toastSuccess', { Message: 'Job Requeued' });
          })
          .catch(() => {})
          .finally(() => {
            this.getData();
          });
      },
      removeFailedJob () {
        // block a second click while the first is in flight
        this.loading = true;
        RemarketService.removeJob({ jobId: this.jobToRemove.Key })
          .then(() => {
            this.$store.dispatch('toastSuccess', { Message: 'Job removed' });
          })
          .catch(() => {})
          .finally(() => {
            this.jobToRemove = null;
            this.getData();
          });
      },
      runRemarket () {
        if (!this.canRun) {
          return;
        }

        this.loading = true;
        RemarketService.run({ body: { submissionIds: this.parsed.valid, salesChannels: this.selectedSalesChannels } })
          .then((result) => {
            this.$store.dispatch('toastSuccess', { Message: `Queued ${result.Enqueued} remarket job${result.Enqueued === 1 ? '' : 's'}` });
            this.submissionIdsInput = '';
          })
          // on failure the ids stay in the box so the admin can fix them and try again
          .catch(() => {})
          .finally(() => {
            this.loading = false;
            this.getData();
          });
      }
    }
  };
</script>
